
import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ayujalogo from "../Logos/AuthLogo.png";
import CustomDialog from "../../CustomDialog";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));       // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600–900px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));  // < 900px

  const [step, setStep] = useState(1);
  const [identifierType, setIdentifierType] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [maskedContact, setMaskedContact] = useState("");
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const passwordRequest = process.env.REACT_APP_PASSWORD_REQUEST_API;
  const verifyOtp = process.env.REACT_APP_VERIFY_OTP_API;
  const passwordReset = process.env.REACT_APP_PASSWORD_RESET_API;

  const steps = ["Enter Details", "Verify OTP", "Reset Password"];

  const countryCodes = [
    { code: "+91", country: "India" },
    { code: "+1", country: "USA/Canada" },
    { code: "+44", country: "UK" },
    { code: "+61", country: "Australia" },
    { code: "+971", country: "UAE" },
    { code: "+65", country: "Singapore" },
  ];

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => phone.replace(/\D/g, "").length >= 10;

  const buildFinalIdentifier = () => {
    if (identifierType === "phone") {
      const cleaned = identifier.replace(/[\s\-\(\)]/g, "");
      return cleaned.startsWith("+") ? cleaned : countryCode + cleaned;
    }
    return identifier;
  };

  const handleSendOtp = async () => {
    if (!identifier) {
      setValidationMessage(`Please enter your ${identifierType}`);
      setValidationDialogOpen(true);
      return;
    }
    if (identifierType === "email" && !validateEmail(identifier)) {
      setValidationMessage("Please enter a valid email address");
      setValidationDialogOpen(true);
      return;
    }
    if (identifierType === "phone" && !validatePhone(identifier)) {
      setValidationMessage("Please enter a valid phone number (at least 10 digits)");
      setValidationDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(passwordRequest, { identifier: buildFinalIdentifier() });
      if (res.data.success) {
        setDeliveryMethod(res.data.delivery_method || identifierType);
        if (res.data.masked_phone) {
          setMaskedContact(res.data.masked_phone);
        } else if (identifierType === "email") {
          const [username, domain] = identifier.split("@");
          const masked =
            username.length > 3
              ? username.substring(0, 3) + "*".repeat(username.length - 3)
              : "*".repeat(username.length);
          setMaskedContact(`${masked}@${domain}`);
        }
        let message =
          res.data.delivery_method === "sms"
            ? `OTP sent to your mobile number ${res.data.masked_phone || ""}`
            : "OTP sent to your email address";
        setValidationMessage(message);
        setValidationDialogOpen(true);
        setStep(2);
      } else {
        setValidationMessage(res.data.message || "Please try again.");
        setValidationDialogOpen(true);
      }
    } catch (err) {
      setValidationMessage(
        err.response?.data?.message ||
          "Failed to send OTP. Please check your connection and try again."
      );
      setValidationDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      setValidationMessage("Please enter the OTP");
      setValidationDialogOpen(true);
      return;
    }
    if (otp.length < 4 || otp.length > 6) {
      setValidationMessage("OTP should be 4-6 digits");
      setValidationDialogOpen(true);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(verifyOtp, {
        identifier: buildFinalIdentifier(),
        otp,
      });
      if (res.data.success) {
        setValidationMessage("OTP Verified Successfully!");
        setValidationDialogOpen(true);
        setStep(3);
      } else {
        setValidationMessage(res.data.message || "Invalid OTP");
        setValidationDialogOpen(true);
      }
    } catch (err) {
      setValidationMessage(
        err.response?.data?.message || "OTP verification failed. Please try again."
      );
      setValidationDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      setValidationMessage("Please enter new password");
      setValidationDialogOpen(true);
      return;
    }
    if (newPassword.length < 6) {
      setValidationMessage("Password must be at least 6 characters");
      setValidationDialogOpen(true);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(passwordReset, {
        identifier: buildFinalIdentifier(),
        newPassword,
      });
      if (res.data.success) {
        setValidationMessage("Password reset successful!");
        setValidationDialogOpen(true);
        setStep(1);
        setIdentifier("");
        setIdentifierType("email");
        setCountryCode("+91");
        setOtp("");
        setNewPassword("");
        setDeliveryMethod("");
        setMaskedContact("");
        setTimeout(() => navigate("/Login"), 1500);
      } else {
        setValidationMessage(res.data.message || "Password reset failed");
        setValidationDialogOpen(true);
      }
    } catch (err) {
      setValidationMessage(
        err.response?.data?.message || "Password reset failed. Please try again."
      );
      setValidationDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleIdentifierTypeChange = (type) => {
    setIdentifierType(type);
    setIdentifier("");
    if (type === "email") setCountryCode("+91");
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setOtp("");
    setDeliveryMethod("");
    setMaskedContact("");
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          p: isMobile ? 2 : isTablet ? 3 : 5,
          backgroundColor: "#fff",
        }}
      >
        <Card
          sx={{
            borderRadius: "16px",
            width: isMobile ? "100%" : isTablet ? "90%" : "60vw",
            maxWidth: "1000px",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          <Grid container>

            {/* ── Left Section (hidden on mobile) ── */}
            {!isMobile && (
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  background: "linear-gradient(135deg, #004f54, #007a85, #007a85)",
                  color: "white",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  p: isTablet ? 3 : 4,
                  width: isTablet ? "50%" : "60%",
                }}
              >
                {/* Logo */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Box
                    component="img"
                    src={ayujalogo}
                    alt="Ayuja Logo"
                    sx={{ height: isTablet ? "60px" : "80px", width: "auto" }}
                  />
                </Box>

                <Typography
                  variant={isTablet ? "h5" : "h4"}
                  sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}
                >
                  Welcome <br /> Back to Ayuja
                </Typography>

                <Typography
                  variant={isTablet ? "body1" : "h6"}
                  sx={{ mb: 2, textAlign: "center", fontSize: isTablet ? "14px" : undefined }}
                >
                  Your trusted partner in personalized in-home care for every stage of life
                </Typography>

                {/* Feature badges */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: isTablet ? 1.5 : 3,
                    mb: 4,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2">✅ 24/7 Support</Typography>
                  <Typography variant="body2">✅ Certified Caregivers</Typography>
                </Box>

                {/* Emergency Support Box */}
                <Box
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: "12px",
                    p: isTablet ? 2 : 3,
                    textAlign: "center",
                    width: isTablet ? "80%" : "50%",
                    mx: "auto",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    mb={1}
                    fontSize={isTablet ? "13px" : undefined}
                  >
                    Emergency Support Available
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, fontSize: isTablet ? "12px" : undefined }}>
                    Need immediate assistance? Our emergency support team is available 24/7 to help
                    you and your loved ones.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/contactus")}
                    sx={{
                      backgroundColor: "#80ED99",
                      "&:hover": { backgroundColor: "#80ED99" },
                      color: "#22577A",
                      textTransform: "none",
                      borderRadius: "8px",
                      fontSize: isTablet ? "12px" : undefined,
                    }}
                  >
                    Contact Emergency
                  </Button>
                </Box>
              </Grid>
            )}

            {/* ── Right Section (Form) ── */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                backgroundColor: "white",
                p: isMobile ? 3 : isTablet ? 3 : 4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                width: isMobile ? "100%" : isTablet ? "50%" : "40%",
              }}
            >
              {/* Show logo on mobile (left panel hidden) */}
              {isMobile && (
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Box
                    component="img"
                    src={ayujalogo}
                    alt="Ayuja Logo"
                    sx={{ height: "60px", width: "auto" }}
                  />
                </Box>
              )}

              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{ fontWeight: "bold", mb: 1, textAlign: isMobile ? "center" : "left" }}
              >
                Forgot Password
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 3, color: "text.secondary", textAlign: isMobile ? "center" : "left" }}
              >
                Follow the steps below to reset your password
              </Typography>

              {/* Stepper */}
              <Box sx={{ mb: 4 }}>
                <Stepper activeStep={step - 1} alternativeLabel>
                  {steps.map((label, index) => (
                    <Step key={label} completed={step > index + 1}>
                      <StepLabel
                        sx={{
                          "& .MuiStepLabel-label": { fontSize: isMobile ? "0.65rem" : "0.75rem" },
                          "& .MuiStepIcon-root": {
                            color: step > index + 1 ? "#007a85" : "rgba(0,0,0,0.38)",
                          },
                          "& .MuiStepIcon-root.Mui-active": { color: "#007a85" },
                          "& .MuiStepIcon-root.Mui-completed": { color: "#007a85" },
                        }}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              {/* ── Step 1: Enter Email / Phone ── */}
              {step === 1 && (
                <>
                  <FormControl fullWidth margin="normal" size={isMobile ? "small" : "medium"}>
                    <InputLabel>Reset via</InputLabel>
                    <Select
                      value={identifierType}
                      label="Reset via"
                      onChange={(e) => handleIdentifierTypeChange(e.target.value)}
                    >
                      <MenuItem value="email">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <EmailIcon fontSize="small" />
                          <span>Email</span>
                        </Box>
                      </MenuItem>
                      <MenuItem value="phone">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PhoneIcon fontSize="small" />
                          <span>Phone Number</span>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {identifierType === "email" ? (
                    <TextField
                      label="Email Address"
                      placeholder="Enter your registered email"
                      fullWidth
                      margin="normal"
                      size={isMobile ? "small" : "medium"}
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  ) : (
                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <FormControl
                        sx={{ width: isMobile ? "35%" : "30%" }}
                        size={isMobile ? "small" : "medium"}
                      >
                        <InputLabel>Code</InputLabel>
                        <Select
                          value={countryCode}
                          label="Code"
                          onChange={(e) => setCountryCode(e.target.value)}
                        >
                          {countryCodes.map((c) => (
                            <MenuItem key={c.code} value={c.code}>
                              {c.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Phone Number"
                        placeholder="Enter your phone number"
                        fullWidth
                        size={isMobile ? "small" : "medium"}
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: "#007a85",
                      "&:hover": { backgroundColor: "#005f64" },
                      mt: 3,
                      py: isMobile ? 1 : 1.5,
                    }}
                    onClick={handleSendOtp}
                    disabled={loading || !identifier}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Send OTP"}
                  </Button>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      textAlign: "center",
                      color: "text.secondary",
                      fontSize: isMobile ? "12px" : undefined,
                    }}
                  >
                    {identifierType === "email"
                      ? "OTP will be sent to your email address"
                      : "OTP will be sent via SMS to your phone"}
                  </Typography>
                </>
              )}

              {/* ── Step 2: Enter OTP ── */}
              {step === 2 && (
                <>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, textAlign: "center", fontSize: isMobile ? "14px" : undefined }}
                  >
                    {`Enter the OTP sent to ${maskedContact || "your registered contact"}`}
                  </Typography>

                  <TextField
                    label="OTP"
                    placeholder="Enter 6-digit OTP"
                    fullWidth
                    margin="normal"
                    size={isMobile ? "small" : "medium"}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputProps={{ maxLength: 6 }}
                  />

                  <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderColor: "#007a85",
                        color: "#007a85",
                        "&:hover": {
                          borderColor: "#005f64",
                          backgroundColor: "rgba(0,122,133,0.04)",
                        },
                        py: isMobile ? 1 : 1.5,
                      }}
                      onClick={handleBackToStep1}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: "#007a85",
                        "&:hover": { backgroundColor: "#005f64" },
                        py: isMobile ? 1 : 1.5,
                      }}
                      onClick={handleVerifyOtp}
                      disabled={loading || otp.length < 4}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : "Verify OTP"}
                    </Button>
                  </Box>

                  <Box sx={{ mt: 2, textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mb: 1, fontSize: isMobile ? "12px" : undefined }}
                    >
                      Didn't receive OTP?
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => { setOtp(""); handleSendOtp(); }}
                      sx={{ color: "#007a85", textTransform: "none", fontSize: "0.875rem" }}
                      disabled={loading}
                    >
                      Resend OTP
                    </Button>
                  </Box>
                </>
              )}

              {/* ── Step 3: Reset Password ── */}
              {step === 3 && (
                <>
                  <Typography
                    variant="body1"
                    sx={{ mb: 3, textAlign: "center", fontSize: isMobile ? "14px" : undefined }}
                  >
                    Create your new password
                  </Typography>

                  <TextField
                    label="New Password"
                    type="password"
                    placeholder="Enter new password (min 6 characters)"
                    fullWidth
                    margin="normal"
                    size={isMobile ? "small" : "medium"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />

                  <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderColor: "#007a85",
                        color: "#007a85",
                        "&:hover": {
                          borderColor: "#005f64",
                          backgroundColor: "rgba(0,122,133,0.04)",
                        },
                        py: isMobile ? 1 : 1.5,
                      }}
                      onClick={() => setStep(2)}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: "#007a85",
                        "&:hover": { backgroundColor: "#005f64" },
                        py: isMobile ? 1 : 1.5,
                        fontSize: isMobile ? "13px" : undefined,
                      }}
                      onClick={handleResetPassword}
                      disabled={loading || newPassword.length < 6}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Reset Password"
                      )}
                    </Button>
                  </Box>
                </>
              )}

              {/* Back to Login */}
              <Box sx={{ mt: 3, textAlign: "center" }}>
                <Button
                  variant="text"
                  onClick={() => navigate("/Login")}
                  sx={{ color: "#007a85", textTransform: "none", fontSize: isMobile ? "13px" : undefined }}
                >
                  ← Back to Login
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Box>

      <CustomDialog
        open={validationDialogOpen}
        message={validationMessage}
        onClose={() => setValidationDialogOpen(false)}
        title="Message"
        buttonText="OK"
      />
    </>
  );
};

export default ForgotPassword;